import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { SlidersHorizontal } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const clothingType = searchParams.get('type');
  const gender = searchParams.get('gender');
  const [filters, setFilters] = useState({
    category: '',
    search: searchParams.get('search') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    sort: searchParams.get('sort') || '',
    under_10: false,
    nepora_originals: false,
    pickup_today: false
  });

  useEffect(() => {
    fetchProducts();
  }, [category, searchParams, clothingType, gender]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (category) {
        if (category === 'deals') {
          params.append('tags', '$1-$10');
        } else if (category === 'under-20') {
          params.append('max_price', '20');
        } else {
          const categoryMap = {
            'sarees': 'Sarees',
            'jewelry': 'Jewelry',
            'beauty': 'Beauty & Cosmetics',
            'accessories': 'Accessories',
            'traditional-wear': 'Traditional Wear',
            'clothing': 'Clothing',
            'lungi': 'Lungi',
            'home': 'Home & Living'
          };
          params.append('category', categoryMap[category] || category);
        }
      }
      
      // Add clothing type filter
      if (clothingType) {
        params.append('clothing_type', clothingType);
      }
      
      // Add gender filter
      if (gender) {
        params.append('gender', gender);
      }
      
      if (filters.search) params.append('search', filters.search);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.nepora_originals) params.append('nepora_original', 'true');
      if (filters.pickup_today) params.append('pickup_today', 'true');
      if (searchParams.get('featured')) params.append('featured', 'true');
      if (searchParams.get('best_seller')) params.append('best_seller', 'true');
      if (searchParams.get('new_arrival')) params.append('new_arrival', 'true');
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchProducts();
  };

  const getCategoryTitle = () => {
    // Check for max_price filter first (used by "Under $10 Deals" nav link)
    const maxPriceParam = searchParams.get('max_price');
    if (maxPriceParam === '10' && !category) return 'Under $10 Deals';
    
    if (category === 'deals') return 'Deals Under $10';
    if (category === 'under-20') return 'Under $20';
    
    // Handle clothing with gender or type
    if (category === 'clothing') {
      if (gender === 'women') return "Women's Clothing";
      if (gender === 'men') return "Men's Clothing";
      if (clothingType === 'casual') return 'Casual Wear';
      if (clothingType === 'seasonal') return 'Seasonal Wear';
      return 'All Clothing';
    }
    
    const titleMap = {
      'sarees': 'Sarees',
      'jewelry': 'Jewelry',
      'beauty': 'Beauty & Cosmetics',
      'accessories': 'Accessories',
      'traditional-wear': 'Traditional Wear',
      'lungi': 'Lungi',
      'home': 'Home & Living'
    };
    return titleMap[category] || 'Shop All';
  };

  // Clothing subcategories for quick navigation
  const clothingTypes = [
    { name: 'All', type: null, gender: null },
    { name: 'Women', type: null, gender: 'women' },
    { name: 'Men', type: null, gender: 'men' },
    { name: 'Casual', type: 'casual', gender: null },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-4xl font-bold mb-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="shop-title"
          >
            {getCategoryTitle()}
          </h1>
          <p className="text-gray-600" data-testid="product-count">{products.length} products</p>
          
          {/* Clothing Type Tabs */}
          {category === 'clothing' && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="clothing-type-tabs">
              {clothingTypes.map((item) => {
                const isActive = item.gender 
                  ? gender === item.gender 
                  : item.type 
                    ? clothingType === item.type 
                    : !gender && !clothingType;
                
                const href = item.gender 
                  ? `/category/clothing?gender=${item.gender}`
                  : item.type 
                    ? `/category/clothing?type=${item.type}`
                    : '/category/clothing';
                
                return (
                  <Link
                    key={item.name}
                    to={href}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[#D4AF37] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1" data-testid="filters-sidebar">
            <div className="bg-[#F9F9F9] p-6 rounded-lg sticky top-24">
              <div className="flex items-center mb-6">
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                <h3 className="font-semibold text-lg">Filters</h3>
              </div>


              {/* Quick Filters */}
              <div className="mb-6 space-y-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.under_10}
                    onChange={(e) => {
                      handleFilterChange('under_10', e.target.checked);
                      if (e.target.checked) {
                        handleFilterChange('max_price', '10');
                      } else {
                        handleFilterChange('max_price', '');
                      }
                    }}
                    className="mr-2"
                    data-testid="filter-under-10"
                  />
                  <span className="text-sm font-medium">Under $10</span>
                </label>
                
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.nepora_originals}
                    onChange={(e) => handleFilterChange('nepora_originals', e.target.checked)}
                    className="mr-2"
                    data-testid="filter-nepora-originals"
                  />
                  <span className="text-sm font-medium">Nepora Originals</span>
                </label>
                
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.pickup_today}
                    onChange={(e) => handleFilterChange('pickup_today', e.target.checked)}
                    className="mr-2"
                    data-testid="filter-pickup-today"
                  />
                  <span className="text-sm font-medium">Pickup Today</span>
                </label>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Price Range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.min_price}
                    onChange={(e) => handleFilterChange('min_price', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="filter-min-price"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.max_price}
                    onChange={(e) => handleFilterChange('max_price', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    data-testid="filter-max-price"
                  />
                </div>
              </div>

              {/* Sort */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Sort By</label>
                <select
                  value={filters.sort}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="filter-sort"
                >
                  <option value="">Featured</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="newest">Newest</option>
                </select>
              </div>

              <button
                onClick={applyFilters}
                className="w-full bg-black text-white py-2 rounded-full hover:bg-gray-800 transition-colors"
                data-testid="apply-filters-button"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12" data-testid="no-products">
                <p className="text-gray-600">No products found</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="products-grid">
                {products.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
